import { useEffect } from 'react';

import { useTeam } from '../context/TeamProvider';
import { useTeamQuery } from '../graphql/team/queryTeam';
import usePlanStore from '../store/plan';
import { PLANS, planForTeam } from './plans';

/**
 * Publishes the current team's plan into `usePlanStore`. Mount once inside the team context,
 * beside `useTenantSync` — `PrivateApp` and `PrivateWizard`.
 *
 * Reads `TEAM_QUERY` with the DEFAULT options, exactly as `useTenantSync` does, so Apollo shares
 * one observable rather than firing a second request. `plan` rides `teamMeta`.
 *
 * Derivation happens during render via the pure `planForTeam`, not in `onCompleted` — that does
 * not fire for cache-only reads, which is what returning to a visited team produces.
 */
export function usePlanSync(): void {
  const { teamId } = useTeam();
  const setPlan = usePlanStore((s) => s.setPlan);
  const { data, error } = useTeamQuery();

  // Fails OPEN, the same call `planFromWirePlan` makes: a failed team query must never lock an
  // existing paying team out of what they already have.
  const plan = error ? PLANS.PREMIUM : planForTeam(teamId, data?.team);

  useEffect(() => {
    setPlan(plan);
  }, [plan, setPlan]);
}
